import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState, useRef } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, Alert } from 'react-bootstrap';
import Spinner from '../../../shared/spinner';
import { Empregador } from '../../../shared/model/empregador';
import { ButtonAlterar, ButtonAtivar, ButtonInativar, ButtonOrgao, ButtonPromotora } from '../../../shared/buttons';
import EmpregadorService from '../../../services/empregadorService';
import ImportacaoService from '../../../services/importacaoService';
import ConfirmModal from '../../../shared/customAlertConfirm';
import { FiltroSimplesDTO } from '../../../shared/dto/filtroSimplesDTO';
import { faFilter, faRefresh, faFileImport, faDownload, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ResponsivePagination from 'react-responsive-pagination';
import { useVerificaPermissao } from '../../../shared/useVerificaPermissao';
import { ProtectedAction } from '../../../AccessControl';
import { ProtectedActionInterno } from '../../../AccessControl';

function EmpregadorComponent() {
  useVerificaPermissao();
  const navigate = useNavigate();
  
  // Services
  const empregadorService: EmpregadorService = new EmpregadorService();
  const importacaoService = new ImportacaoService();

  // States Principais
  const [loading, setLoading] = useState(true);
  const [empregadores, setEmpregadores] = useState<Empregador[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<{ id: any; ativo: boolean } | null>(null);

  // Controle de Painéis
  const [filtros, setFiltros] = useState(false);
  const [importacaoOpen, setImportacaoOpen] = useState(false);

  // Filtros States
  const [codigoFiltro, setCodigoFiltro] = useState('');
  const [nomeFiltro, setNomeFiltro] = useState('');
  
  // Paginação - CORRIGIDO: currentPage inicializado em 1
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // States de Importação
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState<{ type: 'success' | 'danger', text: string } | null>(null);
  const [tipoImportacao, setTipoImportacao] = useState('EMPREGADOR'); 
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Toggle Painéis ---
  const toggleFiltros = () => {
    setFiltros(!filtros);
    setImportacaoOpen(false);
  };

  const toggleImportacao = () => {
    setImportacaoOpen(!importacaoOpen);
    setFiltros(false);
    setImportMessage(null);
    setFileToUpload(null); 
  };

  // --- Listagem --- 
  const listarTodosEmpregadores = async () => {
    try {
      setLoading(true);
      let filtro = new FiltroSimplesDTO();
      filtro.codigo = codigoFiltro;
      filtro.nome = nomeFiltro;
      
      // Ajusta a página para o backend (0-indexed)
      // const pageParaBackend = currentPage > 0 ? currentPage - 1 : 0;


      const responseList = await empregadorService.listarTodosEmpregadoresPaginado(currentPage, pageSize, filtro);
      
      // const responseList = await empregadorService.listarTodosEmpregadoresPaginado(pageParaBackend, pageSize, filtro);
      const jsonList: Empregador[] = responseList.data.content;
      setEmpregadores(jsonList);
      
      // Atualiza o total de páginas retornado pela API
      if (responseList.data.totalPages !== undefined) {
        setTotalPages(responseList.data.totalPages);
      }
      
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  }

  const ativarInativarEmpregador = async (id: any, ativo: boolean) => {
    setCurrentAction({ id, ativo });
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (currentAction) {
      const { id, ativo } = currentAction;
      try {
        await empregadorService.ativarInativarEmpregador(id);
        listarTodosEmpregadores(); 
      } catch (err) {
        console.error(err);
      } finally {
        setConfirmOpen(false);
        setCurrentAction(null);
      }
    }
  };

  // --- LÓGICA DE IMPORTAÇÃO ---
  const handleDownloadModelo = async () => {
    try {
      const response = await importacaoService.baixarModelo(tipoImportacao);
      
      const nomeArquivo = tipoImportacao === 'EMPREGADOR' 
        ? 'modelo_importacao_empregador.xlsx' 
        : 'modelo_importacao_politica_idade.xlsx';

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Erro ao baixar modelo", error);
      setImportMessage({ type: 'danger', text: 'Erro ao baixar o modelo.' });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportMessage(null);
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const extension = file.name.split('.').pop()?.toLowerCase();

      if (extension !== 'xlsx' && extension !== 'csv') {
        setImportMessage({ type: 'danger', text: 'Formato inválido. Apenas .xlsx e .csv são permitidos.' });
        setFileToUpload(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      setFileToUpload(file);
    }
  };

  const handleImportar = async () => {
    if (!fileToUpload) {
      setImportMessage({ type: 'danger', text: 'Selecione um arquivo para importar.' });
      return;
    }

    try {
      setImportLoading(true);
      
      await importacaoService.importar(tipoImportacao, fileToUpload);
      
      setImportMessage({ type: 'success', text: `Importação de ${tipoImportacao === 'EMPREGADOR' ? 'Empregadores' : 'Política de Idade'} realizada com sucesso!` });
      setFileToUpload(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      
      if (tipoImportacao === 'EMPREGADOR') {
          listarTodosEmpregadores();
      }
      
      setTimeout(() => {
        setImportacaoOpen(false);
        setImportMessage(null);
      }, 3000);

    } catch (error: any) {
      console.error("Erro na importação", error);
      const msg = error.response?.data || 'Erro ao processar importação.';
      setImportMessage({ type: 'danger', text: msg });
    } finally {
      setImportLoading(false);
    }
  };

  useEffect(() => {
    listarTodosEmpregadores();
  }, [currentPage]);

  return (
    <div className="position-relative m-md-3">
      <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h5>Parâmetros Cartão &gt; Empregador</h5>
        <div style={{ display: 'flex', gap: '10px' }}>
            <Button id="App-button-cor-principal-escura" onClick={toggleFiltros} active={filtros}>
                <FontAwesomeIcon icon={faFilter} style={{ marginRight: '10px' }} />
                Filtros
            </Button>

            <ProtectedActionInterno>
              <Button variant="outline-primary" onClick={toggleImportacao} active={importacaoOpen}>
                  <FontAwesomeIcon icon={faFileImport} style={{ marginRight: '10px' }} />
                  Importar
              </Button>
            </ProtectedActionInterno>
            <ProtectedAction>
            <Button
            onClick={() => navigate('/parametros-empregador/inclusao', { state: { id: null } })}
            id="App-button-cor-principal-escura"
            >
            + Novo Empregador
            </Button>
            </ProtectedAction>
        </div>
      </div>

      {/* --- ÁREA DE IMPORTAÇÃO --- */}
      {importacaoOpen && (
          <div className="card card-body mb-4" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
            <h6 className="card-title mb-3">
                <FontAwesomeIcon icon={faFileImport} className="me-2" />
                Importação em Lote
            </h6>
            
            <Row className="align-items-end g-3">
              <Col md={3}>
                 <Form.Label>Tipo de Importação</Form.Label>
                 <Form.Select 
                    value={tipoImportacao} 
                    onChange={(e) => {
                        setTipoImportacao(e.target.value);
                        setImportMessage(null); 
                    }}
                 >
                    <option value="EMPREGADOR">Empregadores (Cadastro)</option>
                    <option value="EMPREGADOR-IDADE">Política de Idade</option>
                 </Form.Select>
              </Col>

              <Col md={2}>
                <Form.Label>Modelo</Form.Label>
                <Button variant="outline-secondary" className="w-100" onClick={handleDownloadModelo} title="Baixar planilha modelo">
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Baixar
                </Button>
              </Col>
              
              <Col md={4}>
                <Form.Group controlId="formFile">
                  <Form.Label>Selecione o Arquivo</Form.Label>
                  <Form.Control 
                    type="file" 
                    ref={fileInputRef}
                    onChange={handleFileChange} 
                    accept=".xlsx, .csv"
                  />
                </Form.Group>
              </Col>

              <Col md={3} className="d-flex gap-2">
                 <Button 
                    id="App-button-cor-principal-escura" 
                    className="flex-grow-1"
                    onClick={handleImportar} 
                    disabled={!fileToUpload || importLoading}
                >
                    {importLoading ? <Spinner loading={true} /> : 'Confirmar'}
                </Button>
                <Button variant="secondary" onClick={toggleImportacao}>
                    <FontAwesomeIcon icon={faTimes} />
                </Button>
              </Col>
            </Row>

            {importMessage && (
              <Alert variant={importMessage.type} className="mt-3 mb-0">
                {importMessage.text}
              </Alert>
            )}
            
            <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
                <small>
                    {tipoImportacao === 'EMPREGADOR' 
                        ? 'Importe novos empregadores e suas configurações básicas.' 
                        : 'Importe as regras de idade, fator RMC e valores máximos vinculados aos empregadores.'}
                </small>
            </div>
          </div>
        )}

      {/* --- ÁREA DE FILTROS --- */}
      {filtros ?
        <>
          <Row className="mb-4" style={{ marginTop: '15px' }}>
            <Form.Group as={Col} md='2' controlId="formBasicSelect">
              <Form.Label>Código </Form.Label>
              <input type="text" className="form-control" value={codigoFiltro} onChange={(e) => setCodigoFiltro(e.target.value)} />
            </Form.Group>
            <Form.Group as={Col} md='4' controlId="formBasicSelect">
              <Form.Label>Nome</Form.Label>
              <input type="text" className="form-control" value={nomeFiltro} onChange={(e) => setNomeFiltro(e.target.value)} />
            </Form.Group>
          </Row>
          <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>

            <Button id="App-button-cor-principal-escura" onClick={() => {
              setCurrentPage(1);
              listarTodosEmpregadores();
            }}>
              <FontAwesomeIcon icon={faFilter} style={{ marginRight: '5px' }} />
              Aplicar
            </Button>

            <Button variant="secondary" onClick={() => {
                setCodigoFiltro(''); 
                setNomeFiltro('');
                setCurrentPage(1);
                listarTodosEmpregadores();
            }}>
               <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '5px' }} />
               Limpar
            </Button>
          </div>
        </>
        : null}

      {loading ? <Spinner loading={loading} /> : (
        <div className="table-responsive">
          <div>
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th scope="col" id="App-header-table">Código</th>
                  <th scope="col" id="App-header-table">Nome</th>
                  <th scope="col" id="App-header-table">CNPJ</th>
                  <th scope="col" id="App-header-table">Código Externo Banco</th>
                  <th scope="col" id="App-header-table">Código Motor</th>
                  <ProtectedAction>
                    <th scope="col" id="App-header-table">Alteração</th>
                  </ProtectedAction>
                  <th scope="col" id="App-header-table">Orgãos</th>
                  <th scope="col" id="App-header-table">Origens</th>
                  <ProtectedAction>
                    <th scope="col" id="App-header-table">Status</th>
                  </ProtectedAction>  
                </tr>
              </thead>
              <tbody>
                {empregadores.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.nome}</td>
                    <td>{item.cnpj}</td>
                    <td>{item.codigoExternoBanco}</td>
                    <td>{item.codigoMotor}</td>
                    <ProtectedAction>
                      <td>
                        <Link to={`/parametros-empregador/alteracao`} state={{ id: item.id }}>
                          <ButtonAlterar />
                        </Link>
                      </td>
                    </ProtectedAction>
                    <td>
                      <Link to={`/parametros-empregador/orgao`} state={{ idEmpregador: item.id }}>
                        <ButtonOrgao />
                      </Link>
                    </td>
                    <td>
                      <Link to={`/promotora-empregador`} state={{ chamada: 'empregador', idPromotora: 0, idEmpregador: item.id }}>
                        <ButtonPromotora />
                      </Link>
                    </td>
                    <ProtectedAction>
                      <td>
                        {item.ativo ?
                          <ButtonInativar onClick={() => ativarInativarEmpregador(item.id, item.ativo)} />
                          :
                          <ButtonAtivar onClick={() => ativarInativarEmpregador(item.id, item.ativo)} />
                        }
                      </td>
                    </ProtectedAction>  
                  </tr>
                ))}
              </tbody>
            </table>
            
            {totalPages > 0 && (
              <ResponsivePagination
                current={currentPage}
                total={totalPages}
                onPageChange={setCurrentPage} 
              />
            )}
            
            {confirmOpen && (<ConfirmModal message={currentAction?.ativo ? 'Tem certeza que deseja inativar este item?' : 'Tem certeza que deseja ativar este item?'} onConfirm={handleConfirm} onCancel={() => { setConfirmOpen(false); setCurrentAction(null) }} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default EmpregadorComponent;